/**
 * Auth Token Manager
 * Stores access token in memory (not localStorage) for security
 * Refresh token is handled via httpOnly cookie by the server
 */

// In-memory token storage (cleared on page refresh, which is expected)
let accessToken: string | null = null;
let refreshPromise: Promise<string | null> | null = null;

// Session flag keys
const SESSION_FLAG_KEY = 'wbb_has_session'; // localStorage - persists across browser close
const SESSION_FLAG_KEY_TEMP = 'wbb_has_session_temp'; // sessionStorage - cleared on browser close

/**
 * Token manager for secure token handling
 */
export const tokenManager = {
  /**
   * Get current access token
   */
  getToken(): string | null {
    return accessToken;
  },

  /**
   * Set access token (call after login or refresh)
   * @param token - The access token
   * @param remember - If true, persist session flag in localStorage, otherwise sessionStorage
   */
  setToken(token: string, remember?: boolean): void {
    accessToken = token;
    // Set session flag based on remember preference
    try {
      if (remember === true) {
        // "Remember me" checked - use localStorage (persists across browser close)
        localStorage.setItem(SESSION_FLAG_KEY, '1');
        sessionStorage.removeItem(SESSION_FLAG_KEY_TEMP);
      } else if (remember === false) {
        // "Remember me" unchecked - use sessionStorage (cleared on browser close)
        sessionStorage.setItem(SESSION_FLAG_KEY_TEMP, '1');
        localStorage.removeItem(SESSION_FLAG_KEY);
      }
      // If remember is undefined (refresh flow), don't change storage flags
    } catch {
      // Storage might not be available
    }
  },

  /**
   * Clear access token (call on logout)
   */
  clearToken(): void {
    accessToken = null;
    refreshPromise = null;
    // Clear all session flags
    try {
      localStorage.removeItem(SESSION_FLAG_KEY);
      sessionStorage.removeItem(SESSION_FLAG_KEY_TEMP);
    } catch {
      // Storage might not be available
    }
  },

  /**
   * Check if we have an access token in memory
   */
  hasToken(): boolean {
    return !!accessToken;
  },

  /**
   * Check if we might have a session to restore (from previous login)
   * Checks both localStorage (remember me) and sessionStorage (current session)
   */
  mayHaveSession(): boolean {
    try {
      // Check localStorage first (remember me was checked)
      if (localStorage.getItem(SESSION_FLAG_KEY) === '1') {
        return true;
      }
      // Check sessionStorage (same browser session, remember me unchecked)
      if (sessionStorage.getItem(SESSION_FLAG_KEY_TEMP) === '1') {
        return true;
      }
      return false;
    } catch {
      return false;
    }
  },

  /**
   * Set refresh promise (to prevent multiple refresh calls)
   */
  setRefreshPromise(promise: Promise<string | null>): void {
    refreshPromise = promise;
  },

  /**
   * Get current refresh promise
   */
  getRefreshPromise(): Promise<string | null> | null {
    return refreshPromise;
  },

  /**
   * Clear refresh promise
   */
  clearRefreshPromise(): void {
    refreshPromise = null;
  },
};

export default tokenManager;
